import { useMemo } from 'react';
import type { TalentBehovItem, Bemanning } from '../services/api';

/**
 * Hierarkisk node-struktur for 3-nivå talent-hierarki
 */
export interface HierarchyNode {
  name: string;
  path: string; // Full path (f.eks. "Foto&Video → Lyd")
  level: number; // 0, 1, eller 2
  children: Map<string, HierarchyNode>;
  talents: TalentBehovItem[];
}

/**
 * Custom hook for å bygge hierarkisk tre-struktur fra talent-behov
 * 
 * Ekstraher duplisert kode fra ResourcePlanning og TalentManagerConcept
 * 
 * @param talentBehov - Liste over talent-behov
 * @returns Hierarkisk tre-struktur
 */
export const useTalentHierarchy = (talentBehov: TalentBehovItem[]) => {
  const hierarchy = useMemo(() => {
    const root = new Map<string, HierarchyNode>();
    
    talentBehov.forEach(behov => {
      const parts = (behov.talent_kategori?.split(' → ') || ['Andre']);
      let currentLevel = root;
      let currentPath = '';
      
      parts.forEach((part, index) => {
        currentPath = currentPath ? `${currentPath} → ${part}` : part;
        
        if (!currentLevel.has(part)) {
          currentLevel.set(part, {
            name: part,
            path: currentPath,
            level: index,
            children: new Map(),
            talents: []
          });
        }
        
        const node = currentLevel.get(part)!;
        
        // Legg til talent på siste nivå
        if (index === parts.length - 1) {
          node.talents.push(behov);
        }
        
        currentLevel = node.children;
      });
    });
    
    return root;
  }, [talentBehov]);
  
  return hierarchy;
};

/**
 * Helper-funksjoner for å telle bemanning per node (rekursivt)
 */
export const createNodeCounter = (bemanning: Bemanning[]) => {
  /**
   * Teller antall bemanning på en node (inkl. barn) basert på filterfunksjon
   * 
   * @param node - Hierarkisk node
   * @param filterFn - Filterfunksjon for å telle spesifikke personer
   * @returns Antall personer som matcher filteret
   */
  const countForNode = (node: HierarchyNode, filterFn: (b: Bemanning) => boolean): number => {
    let count = node.talents.reduce((sum, talent) => {
      return sum + bemanning.filter(b => b.talent_navn === talent.talent_navn && filterFn(b)).length;
    }, 0);
    
    node.children.forEach(child => {
      count += countForNode(child, filterFn);
    });
    
    return count;
  };
  
  return { countForNode };
};

